import { z } from 'zod';
import { BATCH_ACTION_MAX_TAG_LENGTH, BATCH_ACTION_MAX_TAGS, matchesExactTags, sanitizeTagList, } from './batch-actions.js';
export const EXPRESS_MAX_SHIPPING_METHODS = 12;
export const EXPRESS_MAX_SHIPPING_VALUE_LENGTH = 255;
export const ExpressRuleSchema = z.object({
    tags: z.array(z.string().trim().min(1).max(BATCH_ACTION_MAX_TAG_LENGTH)).max(BATCH_ACTION_MAX_TAGS),
    shippingMethods: z.array(z.object({
        field: z.enum(['title', 'code']),
        value: z.string().trim().min(1).max(EXPRESS_MAX_SHIPPING_VALUE_LENGTH),
    }).strict()).max(EXPRESS_MAX_SHIPPING_METHODS),
}).strict();
/** Defensive, bounded read sanitizer. NULL and malformed roots never elect a rule. */
export function sanitizeExpressRule(raw) {
    const empty = { tags: [], shippingMethods: [] };
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return empty;
    const value = raw;
    const shippingMethods = [];
    const seen = new Set();
    if (Array.isArray(value.shippingMethods)) {
        for (const item of value.shippingMethods.slice(0, EXPRESS_MAX_SHIPPING_METHODS * 4)) {
            if (shippingMethods.length >= EXPRESS_MAX_SHIPPING_METHODS)
                break;
            if (!item || typeof item !== 'object' || Array.isArray(item))
                continue;
            const pick = item;
            if (pick.field !== 'title' && pick.field !== 'code')
                continue;
            if (typeof pick.value !== 'string')
                continue;
            const trimmed = pick.value.trim();
            if (!trimmed || trimmed.length > EXPRESS_MAX_SHIPPING_VALUE_LENGTH)
                continue;
            const key = JSON.stringify([pick.field, trimmed]);
            if (seen.has(key))
                continue;
            seen.add(key);
            shippingMethods.push({ field: pick.field, value: trimmed });
        }
    }
    return { tags: sanitizeTagList(value.tags) ?? [], shippingMethods };
}
/** Undefined means unavailable/degraded; [] is an observed empty projection.
 * Preserve every line (including equal titles with distinct codes).
 */
export function normalizeShippingLines(raw) {
    if (!Array.isArray(raw))
        return undefined;
    const lines = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object' || Array.isArray(item))
            return undefined;
        const line = item;
        if ((line.title != null && typeof line.title !== 'string') ||
            (line.code != null && typeof line.code !== 'string'))
            return undefined;
        lines.push({
            title: typeof line.title === 'string' ? line.title.trim() : null,
            code: typeof line.code === 'string' ? line.code.trim() : null,
        });
    }
    return lines;
}
/** Pure reference evaluator; the DB trigger is authoritative for persisted flags. */
export function matchesExpressRule(rawRule, order) {
    const rule = sanitizeExpressRule(rawRule);
    if (matchesExactTags(rule.tags, order.tags))
        return true;
    const lines = normalizeShippingLines(order.shippingLines) ?? [];
    return rule.shippingMethods.some((pick) => lines.some((line) => line[pick.field] === pick.value));
}
